/*
  MWM per-variant waitlist (snippets/mwm-waitlist-form.liquid, rendered by buy-buttons).

  - Shows the waitlist instead of Add to cart while the selected variant is unavailable, and the
    normal buttons as soon as it is available. Quantity is hidden while the waitlist shows.
  - Keeps the helper line ("We'll email you when Navy, size M is ready.") and the hidden
    contact[tags] input in step with the selected variant.
  - Submits the customer form inline (fetch), so the shopper stays on the same product, variant and
    scroll position. Falls back to a normal form post when Shopify asks for a bot challenge.
*/
(() => {
  if (window.mwmWaitlist) return;

  const HIDDEN_CLASS = 'mwm-is-hidden';
  const slug = (value) => String(value ?? '').toLowerCase().trim().split(' ').join('-');

  function setHidden(element, hidden) {
    if (!element) return;
    element.classList.toggle(HIDDEN_CLASS, hidden);
    element.hidden = hidden;
  }

  const rootsFor = (sectionId) =>
    Array.from(document.querySelectorAll('[data-mwm-waitlist]')).filter(
      (root) => root.dataset.section === sectionId
    );

  function tagsFor(root, variant) {
    const handle = root.dataset.productHandle;
    const colorIndex = Number(root.dataset.colorIndex);
    const sizeIndex = Number(root.dataset.sizeIndex);
    const options = variant.options || [];
    const variantSlug =
      colorIndex >= 0 && sizeIndex >= 0
        ? `${slug(options[colorIndex])}:${slug(options[sizeIndex])}`
        : options.map(slug).join(':');
    return ['waitlist', `waitlist:${handle}`, `waitlist:${handle}:${variantSlug}`].join(',');
  }

  function helperFor(root, variant) {
    const colorIndex = Number(root.dataset.colorIndex);
    const sizeIndex = Number(root.dataset.sizeIndex);
    const options = variant.options || [];
    if (colorIndex >= 0 && sizeIndex >= 0) {
      return root.dataset.helperTemplate
        .replace('[color]', options[colorIndex] ?? '')
        .replace('[size]', options[sizeIndex] ?? '');
    }
    return root.dataset.helperGeneric.replace('[variant]', variant.title || options.join(' / '));
  }

  function setStatus(root, message, { error = false } = {}) {
    const status = root.querySelector('[data-mwm-waitlist-status]');
    if (!status) return;
    status.replaceChildren();
    if (!message) return;
    const paragraph = document.createElement('p');
    paragraph.className = error ? 'mwm-waitlist__error' : 'mwm-waitlist__success';
    paragraph.textContent = message;
    status.appendChild(paragraph);
  }

  function showForm(root) {
    setHidden(root.querySelector('[data-mwm-waitlist-body]'), false);
    const email = root.querySelector('[data-mwm-waitlist-email]');
    email?.removeAttribute('aria-invalid');
    setStatus(root, '');
  }

  // Show the waitlist or the buy buttons for `variant` (null = no such variant).
  function render(sectionId, variant) {
    rootsFor(sectionId).forEach((root) => {
      const buy = root.parentElement?.querySelector(`[data-mwm-buy][data-section="${sectionId}"]`);
      const quantity = document.getElementById(`Quantity-Form-${sectionId}`);
      const waitlist = Boolean(variant) && variant.available === false;

      setHidden(root, !waitlist);
      setHidden(buy, waitlist);
      setHidden(quantity, waitlist);
      if (!waitlist) return;

      const tagsInput = root.querySelector('[data-mwm-waitlist-tags]');
      const nextTags = tagsFor(root, variant);
      const variantChanged = tagsInput && tagsInput.value !== nextTags;
      if (tagsInput) tagsInput.value = nextTags;

      const helper = root.querySelector('[data-mwm-waitlist-helper]');
      if (helper) helper.textContent = helperFor(root, variant);

      // A signup is for one variant; offer the form again when the shopper picks another.
      if (variantChanged) showForm(root);
    });
  }

  async function submit(root, form) {
    if (form.dataset.submitting) return;
    form.dataset.submitting = 'true';
    const button = form.querySelector('[data-mwm-waitlist-submit]');
    const email = form.querySelector('[data-mwm-waitlist-email]');
    button?.setAttribute('aria-busy', 'true');
    setStatus(root, '');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });
      const finalUrl = new URL(response.url, window.location.origin);

      if (finalUrl.pathname.includes('/challenge')) {
        // Shopify wants a bot challenge: let the browser post the form normally.
        HTMLFormElement.prototype.submit.call(form);
        return;
      }

      if (finalUrl.searchParams.get('customer_posted') === 'true') {
        setHidden(root.querySelector('[data-mwm-waitlist-body]'), true);
        const status = root.querySelector('[data-mwm-waitlist-status]');
        setStatus(root, status?.dataset.success || '');
        status?.focus({ preventScroll: true });
        return;
      }

      // Error: Shopify re-renders the page with the form's errors; show them inline.
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const serverError = html.querySelector(`#${CSS.escape(form.id)} [data-mwm-waitlist-error]`);
      const message = serverError?.textContent.replace(/\s+/g, ' ').trim() || root.dataset.errorFallback;
      setStatus(root, message, { error: true });
      email?.setAttribute('aria-invalid', 'true');
      email?.focus({ preventScroll: true });
    } catch (error) {
      console.error(error);
      setStatus(root, root.dataset.errorFallback, { error: true });
    } finally {
      delete form.dataset.submitting;
      button?.removeAttribute('aria-busy');
    }
  }

  function bind(root) {
    if (root.dataset.mwmWaitlistBound) return;
    root.dataset.mwmWaitlistBound = 'true';

    const disclosure = root.querySelector('[data-mwm-waitlist-disclosure]');
    disclosure?.addEventListener('toggle', () => {
      if (disclosure.open) root.querySelector('[data-mwm-waitlist-email]')?.focus();
    });

    const form = root.querySelector('form');
    form?.addEventListener('submit', (event) => {
      if (!form.checkValidity()) return; // let the browser show its validation message
      event.preventDefault();
      submit(root, form);
    });
  }

  function init() {
    document.querySelectorAll('[data-mwm-waitlist]').forEach(bind);

    // After a non-inline post (bot challenge path) Shopify redirects back with ?customer_posted=true.
    // Drop it from the address bar but keep ?variant= so a refresh doesn't show a stale message.
    const url = new URL(window.location.href);
    if (url.searchParams.has('customer_posted')) {
      url.searchParams.delete('customer_posted');
      url.hash = '';
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { sectionId, variant } }) => {
      document.querySelectorAll('[data-mwm-waitlist]').forEach(bind);
      render(sectionId, variant);
    });
  }

  // Option combination with no variant: Dawn shows "Unavailable" on the buy button; show that, not the waitlist.
  const ProductInfo = customElements.get('product-info');
  if (ProductInfo && !ProductInfo.prototype.mwmWaitlistPatched) {
    const originalSetUnavailable = ProductInfo.prototype.setUnavailable;
    ProductInfo.prototype.setUnavailable = function (...args) {
      originalSetUnavailable.apply(this, args);
      render(this.dataset.section, null);
    };
    ProductInfo.prototype.mwmWaitlistPatched = true;
  }

  window.mwmWaitlist = { render, tagsFor, helperFor };
})();
